import React, { useEffect, useState, useContext } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import { resolveAssetUrl } from "../../api/config";

const formatEventDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString();
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatEventDate(end);
  if (!end || end === start) return formatEventDate(start);
  return `${formatEventDate(start)} to ${formatEventDate(end)}`;
};

const MyRegistrations = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const statusLabel = (row) => {
    if (row.status !== "Pending") return row.status;
    const stage = (row.approval_stage || "District").toString().trim();
    return `Pending at ${stage}`;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchRegistrations = async () => {
      if (authLoading) return;

      if (!user?.id) {
        if (!cancelled) {
          setError("Unable to load your profile. Please login again.");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError("");
      try {
        const data = await API.fetchPlayerRegistrations(user.id);
        if (!cancelled) setRegistrations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading registrations:", err);
        if (!cancelled) setError("Failed to load registrations");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRegistrations();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <p className="text-gray-500 text-lg">Loading your registrations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mt-6 sm:mt-8 mobile-page-pad">
      <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-6 text-center">
        My Event Registrations
      </h1>

      {registrations.length === 0 ? (
        <div className="text-center text-gray-500">
          You have not registered for any events yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 rounded-lg text-xs sm:text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="p-3 border font-semibold">Event</th>
                <th className="p-3 border font-semibold">Sport</th>
                <th className="p-3 border font-semibold">Games</th>
                <th className="p-3 border font-semibold">Date</th>
                <th className="p-3 border font-semibold">Location</th>
                <th className="p-3 border font-semibold">Status</th>
                <th className="p-3 border font-semibold">Payment Proof</th>
                <th className="p-3 border font-semibold">ID Card</th>
              </tr>
            </thead>

            <tbody>
              {registrations.map((r, index) => (
                <tr
                  key={index}
                  className="text-center hover:bg-gray-50 transition"
                >
                  <td className="p-3 border font-medium text-gray-800">
                    {r.event_title}
                  </td>
                  <td className="p-3 border">{r.sport_name}</td>
                  <td className="p-3 border text-sm">{r.games || "-"}</td>
                  <td className="p-3 border">
                    {formatEventDateRange(r.date, r.event_end_date)}
                  </td>
                  <td className="p-3 border">{r.location}</td>

                  {/* ✅ Status */}
                  <td className="p-3 border">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        r.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : r.status === "Rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {statusLabel(r)}
                    </span>
                  </td>

                  {/* ✅ Payment Proof */}
                  <td className="p-3 border">
                    {r.payment_proof ? (
                      r.payment_proof.match(/\.(jpg|jpeg|png)$/i) ? (
                        <img
                          src={resolveAssetUrl(r.payment_proof, { folder: "payments" })}
                          alt="Payment"
                          className="w-12 h-12 object-cover mx-auto rounded cursor-pointer border"
                          onClick={() =>
                            window.open(
                              resolveAssetUrl(r.payment_proof, { folder: "payments" }),
                              "_blank"
                            )
                          }
                        />
                      ) : (
                        <a
                          href={resolveAssetUrl(r.payment_proof, { folder: "payments" })}
                          target="_blank"
                          download
                          rel="noreferrer"
                          className="text-indigo-900 font-semibold hover:underline"
                        >
                          View PDF
                        </a>
                      )
                    ) : (
                      <span className="text-gray-500">Not uploaded</span>
                    )}
                  </td>

                  {/* ✅ Conditional ID Download Button */}
                  <td className="p-3 border">
                    {r.status === "Approved" ? (
                      <button
                        onClick={() => API.downloadPlayerIdCard(r.event_id)}
                        className="bg-indigo-900 hover:bg-indigo-950 text-white text-xs px-3.5 py-1.5 rounded-lg shadow-sm font-semibold transition"
                      >
                        Download ID
                      </button>
                    ) : (
                      <span className="text-gray-400 text-sm">Not Available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MyRegistrations;
